import React, { Fragment, useState } from 'react';
import * as Yup from 'yup';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CashManagementCrumbs } from 'front-office/constants/cash-management-crumbs';
import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import { DateTime } from 'luxon';
import { FormikErrors, useFormik } from 'formik';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useNavigate } from 'react-router-dom';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { PmiButton } from 'shell/components/shared/button';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { ResponseCertificateGeneric } from 'shell/models/response-certificate-generic';
import { HttpResponse } from 'shell/models/http-response';

export interface CheckServiceForm {
  service?: string;
  operator?: string;
  extractionDateFrom?: DateTime | null;
  extractionDateTo?: DateTime | null;
  closeCash?: boolean;
  summary?: string;
}

const CashManagementClosingMonthly = (): React.ReactElement => {
  const printSummary = useHttpClient<HttpResponse<ResponseCertificateGeneric>>(
    `${__CERTIFICATION_API_URL__}/certificazione/controllo-cassa/stampa-chiusura-servizi`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const navigate = useNavigate();

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const onSubmit = (form: CheckServiceForm) => {
    (
      printSummary({
        data: {
          chiusuraDiCassa: form.closeCash,
          dataEstrazioneA: form.extractionDateTo?.toFormat('yyyy-MM-dd'),
          dataEstrazioneDa: form.extractionDateFrom?.toFormat('yyyy-MM-dd'),
        },
      }) as ResponsePromise<HttpResponse<ResponseCertificateGeneric>>
    ).then((response) => {
      setPdfModal({
        data: response?.data.payload?.contenutoPdfStampa || '',
        name: 'Gestione_Cassa',
        open: true,
      });
    });
  };
  const closingMonthlyForm = useFormik({
    initialValues: {
      service: '',
      operator: '',
      extractionDateFrom: null,
      extractionDateTo: null,
      closeCash: true,
      summary: '',
    },
    onSubmit,
    validationSchema: Yup.object().shape({
      extractionDateFrom: Yup.date()
        .typeError('Inserire una data valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      extractionDateTo: Yup.date()
        .typeError('Inserire una data valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      closeCash: Yup.bool().required('Il campo è obbligatorio'),
    }),
  });

  return (
    <Fragment>
      <Breadcrumbs
        items={[
          ...CashManagementCrumbs,
          { name: 'Stampa chiusura di cassa mensile di tutti i servizi' },
        ]}
      />
      <Card raised className="pmi-my-3">
        <form onSubmit={closingMonthlyForm.handleSubmit}>
          <CardContent style={{ padding: '24px' }}>
            <Typography gutterBottom variant="h5" component="h2">
              Controllo / chiusura di cassa di tutti i servizi
            </Typography>
            <div style={{ padding: '24px 0 0 0' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <DatePicker
                    label="Data inizio validità"
                    aria-label="Data inizio validità"
                    value={closingMonthlyForm.values.extractionDateFrom || null}
                    onChange={(date) => {
                      closingMonthlyForm.setFieldValue('extractionDateFrom', date);
                    }}
                    renderInput={(params) => (
                      <PmiTextField
                        id="extractionDateFrom"
                        {...params}
                        fullWidth
                        error={
                          !!closingMonthlyForm.touched.extractionDateFrom &&
                          !!closingMonthlyForm.errors.extractionDateFrom
                        }
                        helperText={
                          closingMonthlyForm.errors.extractionDateFrom as FormikErrors<string>
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <DatePicker
                    label="Data fine validità"
                    aria-label="Data fine validità"
                    value={closingMonthlyForm.values.extractionDateTo || null}
                    onChange={(date) => {
                      closingMonthlyForm.setFieldValue('extractionDateTo', date);
                    }}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        id="extractionDateTo"
                        fullWidth
                        error={
                          !!closingMonthlyForm.touched.extractionDateTo &&
                          !!closingMonthlyForm.errors.extractionDateTo
                        }
                        helperText={
                          closingMonthlyForm.errors.extractionDateTo as FormikErrors<string>
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </div>
          </CardContent>
          <CardActions style={{ padding: '24px 24px 16px 24px' }}>
            <PmiButton id="submitForm" variant="contained" color="primary" type="submit">
              Conferma
            </PmiButton>
            <PmiButton
              id="goBack"
              variant="outlined"
              color="cdmBrand"
              onClick={() => {
                navigate('/sportello/gestione-cassa');
              }}
            >
              Annulla
            </PmiButton>
          </CardActions>
        </form>
      </Card>
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Gestione cassa"
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
        }}
      />
    </Fragment>
  );
};

CashManagementClosingMonthly.displayName = 'Gestione cassa navigation menu';

export default CashManagementClosingMonthly;
